import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Award, MapPin, Activity, Shield, FileCheck, Users } from 'lucide-react';

const SITE_URL = 'https://ninjathlete.com';

function injectJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement('script'); el.id = id; el.type = 'application/ld+json'; document.head.appendChild(el); }
  el.textContent = JSON.stringify(data);
  return el;
}

export default function AuthorPage() {
  useEffect(() => {
    document.title = 'About NinjAthlete | Our Team & Editorial Standards';
    const orgSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "NinjAthlete",
      "url": SITE_URL,
      "logo": `${SITE_URL}/logo.png`,
      "description": "Evidence-based biohacking, peptide therapy education, HYROX training, and performance optimization. Science-backed guides with cited research for athletes who refuse to settle.",
      "knowsAbout": ["HYROX Training Programming", "Peptide Therapy Protocols", "Biohacking", "Performance Nutrition", "Longevity Science"],
      "sameAs": ["https://instagram.com/ninjathlete", "https://twitter.com/ninjathlete"],
      "address": { "@type": "PostalAddress", "addressLocality": "Dallas", "addressRegion": "TX", "addressCountry": "US" }
    };
    const script = injectJsonLd('about-org-schema', orgSchema);
    return () => { if (script?.parentNode) script.parentNode.removeChild(script); };
  }, []);

  return (
    <main className="pt-20 min-h-screen">
      <div className="container-ninja section-spacing max-w-3xl">
        <Link to="/blog" className="inline-flex items-center gap-2 text-zinc-500 hover:text-white transition-colors text-sm mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to Blog
        </Link>

        <div className="mb-12">
          <h1 className="text-4xl sm:text-5xl uppercase tracking-wider text-white mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="about-heading">
            About NinjAthlete
          </h1>
          <p className="text-zinc-400 text-base leading-relaxed max-w-xl">
            Science-backed performance optimization for athletes who refuse to settle. We publish evidence-based guides on HYROX training, peptide therapy, biohacking, and longevity science.
          </p>
        </div>

        {/* What We Cover */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
          {[
            { icon: Activity, title: 'HYROX Training', desc: 'Race-day programming, station breakdowns, pacing strategy, and gym directories for competitors at every level.' },
            { icon: Shield, title: 'Peptide Education', desc: 'Evidence-based guides on peptide therapy protocols — mechanisms, dosing, safety profiles, and cited research.' },
            { icon: Award, title: 'Biohacking & Longevity', desc: 'Cold exposure, red light therapy, sleep optimization, and performance nutrition backed by peer-reviewed science.' },
            { icon: MapPin, title: 'Dallas Fitness Community', desc: 'Local gym directories, run clubs, HYROX events, and athlete resources for the DFW metroplex.' },
          ].map(({ icon: Icon, title, desc }) => (
            <div key={title} className="bg-zinc-900/60 border border-zinc-800 p-5">
              <Icon className="w-5 h-5 text-[#E63946] mb-2" />
              <h3 className="text-white text-sm font-semibold mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>{title}</h3>
              <p className="text-zinc-500 text-xs leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>

        {/* Editorial Standards */}
        <div className="bg-zinc-900/60 border border-zinc-800 p-6 mb-12">
          <div className="flex items-center gap-3 mb-4">
            <FileCheck className="w-5 h-5 text-[#E63946]" />
            <h2 className="text-xl uppercase tracking-wider text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
              Editorial Standards
            </h2>
          </div>
          <ul className="space-y-3 text-zinc-400 text-sm">
            <li className="flex items-start gap-2"><span className="text-[#E63946] mt-1">&#x25CF;</span> All peptide content follows YMYL editorial guidelines with cited sources (DOI/PubMed links where available)</li>
            <li className="flex items-start gap-2"><span className="text-[#E63946] mt-1">&#x25CF;</span> HYROX training content is based on competition experience and certified coaching methodologies</li>
            <li className="flex items-start gap-2"><span className="text-[#E63946] mt-1">&#x25CF;</span> Content is reviewed and updated quarterly with "Last reviewed" date stamps on all YMYL articles</li>
            <li className="flex items-start gap-2"><span className="text-[#E63946] mt-1">&#x25CF;</span> Clear disclaimers on all health-related content — educational purposes only, not medical advice</li>
            <li className="flex items-start gap-2"><span className="text-[#E63946] mt-1">&#x25CF;</span> Peptide articles are medically reviewed when a qualified reviewer is assigned</li>
          </ul>
          <Link to="/editorial-policy" className="inline-flex items-center gap-1 text-[#E63946] text-xs mt-4 hover:text-white transition-colors">
            Read our full editorial policy <ArrowLeft className="w-3 h-3 rotate-180" />
          </Link>
        </div>

        {/* Medical Review Process */}
        <div className="bg-zinc-900/60 border border-zinc-800 p-6 mb-12">
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-5 h-5 text-[#E63946]" />
            <h2 className="text-xl uppercase tracking-wider text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
              Medical Review Process
            </h2>
          </div>
          <p className="text-zinc-400 text-sm leading-relaxed mb-3">
            Our peptide and health-related articles go through a structured review process. Articles flagged as YMYL (Your Money or Your Life) are reviewed by qualified medical professionals when available, and display a "Reviewed by" byline with the reviewer's credentials.
          </p>
          <p className="text-zinc-500 text-xs">
            All health content includes clear medical disclaimers and is presented for educational purposes only.
          </p>
        </div>

        {/* Connect */}
        <div className="text-center text-zinc-600 text-xs">
          Questions? <Link to="/contact" className="text-zinc-400 hover:text-white transition-colors underline">Contact us</Link>
        </div>
      </div>
    </main>
  );
}
